import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, Settings, Trash2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import LegalTabs from "@/components/LegalTabs";

const Section = ({ title, id, children, testId }) => (
    <section id={id} className="scroll-mt-24" data-testid={testId}>
        <h2 className="font-heading font-medium text-2xl tracking-tight text-foreground mb-3">{title}</h2>
        <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">{children}</div>
    </section>
);

const List = ({ children }) => (
    <ul className="list-disc pl-5 space-y-1.5">{children}</ul>
);

export default function Cookies() {
    return (
        <div className="bg-background" data-testid="cookies-page">
            <Helmet>
                <title>Cookies · Simonara Talent Solutions</title>
                <meta name="description" content="How Simonara uses first-party cookies and PostHog analytics — and how you can manage or disable them." />
                <link rel="canonical" href="/cookies" />
            </Helmet>

            <section className="pt-16 pb-2">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><Cookie size={11} /> Legal</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">
                        Cookies
                    </h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        Short and honest — how we use cookies on simonaratalent.us, what we track, and how you can turn it off. Version 2026-10-14.
                    </p>
                </div>
            </section>
            <LegalTabs />

            <section className="pb-20 pt-8">
                <div className="mx-auto max-w-4xl px-6 space-y-10">

                    <Section id="what" title="What are cookies?" testId="cookies-what">
                        <p>
                            Cookies are small text files a website stores in your browser. They let a site remember useful state
                            between page loads — like your login session, dark-mode preference, or which cohort of a redesign
                            you've been shown.
                        </p>
                        <p>
                            <strong className="text-foreground">Simonara only uses first-party cookies</strong> — cookies set by
                            <span className="mono-score"> simonaratalent.us</span> itself. We do not run third-party ad trackers,
                            we do not share cookie IDs with data brokers, and we do not sell your browsing behaviour.
                        </p>
                    </Section>

                    <Section id="what-we-set" title="What we actually set" testId="cookies-what-we-set">
                        <List>
                            <li>
                                <strong className="text-foreground">Session cookies</strong> — keep you signed in as a professional,
                                employer, or admin. Cleared when you log out or your session expires (7 days).
                            </li>
                            <li>
                                <strong className="text-foreground">Preference cookies</strong> — remember your theme (light/dark),
                                dismissed banners, and which onboarding step you've reached.
                            </li>
                            <li>
                                <strong className="text-foreground">Security cookies</strong> — CSRF tokens and rate-limit fingerprints
                                that protect your account from cross-site attacks.
                            </li>
                        </List>
                    </Section>

                    <Section id="analytics" title="Analytics — PostHog" testId="cookies-analytics">
                        <p>
                            We use <strong className="text-foreground">PostHog</strong> as our first-party product-analytics tool.
                            PostHog helps us understand which parts of the platform get used, where users get stuck, and which
                            experiments improve the hiring loop. It runs in our own infrastructure — never through a third-party ad
                            network.
                        </p>
                        <List>
                            <li>PostHog sets one first-party cookie (<span className="mono-score">ph_*</span>) to identify a browser across sessions.</li>
                            <li>We only capture product events (clicks, page views, feature usage). We <strong className="text-foreground">do not</strong> record your keystrokes, form values, or resume contents.</li>
                            <li>Personal data (email, name) is captured only after you sign in and is scoped to your Simonara account so we can help you if you email support.</li>
                            <li>We never send PostHog data to advertisers or data brokers.</li>
                        </List>
                    </Section>

                    <Section id="consent" title="How you control analytics on Simonara" testId="cookies-consent-flow">
                        <p>
                            On your first visit, a <strong className="text-foreground">cookie banner</strong> appears at the
                            bottom-left of the screen. It explains, in two sentences, that we run PostHog analytics and offers you
                            three choices:
                        </p>
                        <List>
                            <li>
                                <strong className="text-foreground">Accept</strong> — your decision is stored in your browser as
                                <span className="mono-score"> simonara_cookie_consent = &quot;accepted&quot;</span>, and PostHog continues to run.
                            </li>
                            <li>
                                <strong className="text-foreground">Reject</strong> — your decision is stored as
                                <span className="mono-score"> simonara_cookie_consent = &quot;rejected&quot;</span>, PostHog is opted out
                                immediately via <span className="mono-score">posthog.opt_out_capturing()</span>, and the banner does not
                                reappear on future visits.
                            </li>
                            <li>
                                <strong className="text-foreground">Cookie settings</strong> — opens this page so you can read the
                                full policy before deciding.
                            </li>
                        </List>
                        <p>
                            Changed your mind? Click <strong className="text-foreground">Cookie preferences</strong> at the bottom of
                            any page to clear your stored decision and bring the banner back. Clicking
                            <strong className="text-foreground"> Do Not Sell or Share</strong> in the footer sets your decision to
                            rejected AND opens the data-request form in one step.
                        </p>
                        <p className="inline-flex items-center gap-2 text-foreground font-medium mt-4"><Shield size={14} /> Global Privacy Control (GPC) auto-honour</p>
                        <p>
                            If your browser sends the <span className="mono-score">Sec-GPC</span> header — or equivalently sets
                            <span className="mono-score"> navigator.globalPrivacyControl = true</span> (Brave, Firefox with the GPC
                            setting on, the DuckDuckGo browser, and most privacy-focused browsers do this by default) — Simonara
                            treats you as having rejected analytics the moment the page loads. No banner is shown, PostHog is never
                            initialized, and <span className="mono-score">simonara_cookie_consent = &quot;rejected&quot;</span> is written on
                            your behalf. You do not need to click anything. This is enforced in the client bootstrap
                            (<span className="mono-score">src/lib/consent.js</span>) and overrides any previously stored
                            &quot;accepted&quot; value from the same browser.
                        </p>
                    </Section>

                    <Section id="manage" title="How to manage or disable cookies" testId="cookies-manage">
                        <p className="inline-flex items-center gap-2 text-foreground font-medium"><Settings size={14} /> In your browser</p>
                        <List>
                            <li><strong className="text-foreground">Chrome / Edge:</strong> Settings → Privacy and security → Third-party cookies (or Cookies and other site data).</li>
                            <li><strong className="text-foreground">Firefox:</strong> Settings → Privacy &amp; Security → Cookies and Site Data.</li>
                            <li><strong className="text-foreground">Safari:</strong> Settings → Privacy → Manage Website Data.</li>
                            <li><strong className="text-foreground">Do Not Track:</strong> We honour the browser DNT header — if it's on, we skip analytics events for your session.</li>
                        </List>
                        <p className="inline-flex items-center gap-2 text-foreground font-medium mt-4"><Trash2 size={14} /> Wipe Simonara cookies specifically</p>
                        <p>
                            Open your browser's site-data panel, search <span className="mono-score">simonaratalent.us</span>, and
                            click Delete. You'll be signed out and preference cookies will reset — nothing else on the platform is
                            affected.
                        </p>
                    </Section>

                    <Section id="consequences" title="What breaks if you disable them" testId="cookies-consequences">
                        <List>
                            <li>You'll still be able to browse marketing pages and public job listings anonymously.</li>
                            <li>You <strong className="text-foreground">won't be able to stay signed in</strong> — logins depend on session cookies.</li>
                            <li>Your theme/onboarding preferences will reset on every visit.</li>
                            <li>Analytics events won't fire — which is fine for you, but it means your feedback loops don't reach the product team as quickly.</li>
                        </List>
                    </Section>

                    <Section id="more" title="Tied to our Privacy Policy" testId="cookies-more">
                        <p>
                            Cookies are one piece of a broader story. For the full picture — what personal data we collect, how
                            long we keep it, and how to exercise your GDPR/CCPA rights — read the{" "}
                            <Link to="/privacy" className="underline text-foreground font-medium" data-testid="cookies-link-privacy">
                                Privacy Policy
                            </Link>.
                        </p>
                        <p>
                            Questions? Email{" "}
                            <a href="mailto:privacy@simonaratalent.us" className="underline text-foreground font-medium">privacy@simonaratalent.us</a> — a real human answers.
                        </p>
                    </Section>

                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 inline-flex items-start gap-3">
                        <Shield size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground">
                            <strong>Our promise:</strong> No third-party ad cookies. No cross-site tracking. No data sold — ever.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
